import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const PROT_HANDLE = 'envio-prioritario-seguro';
const KEY_END = 'vl-cart-reserve-end';
const KEY_DECLINED = 'vl-prot-declined';
const KEY_AUTO = 'vl-prot-auto';

const store = {
  get(k) {
    try { return sessionStorage.getItem(k); } catch (e) { return null; }
  },
  set(k, v) {
    try { sessionStorage.setItem(k, v); } catch (e) {}
  },
  del(k) {
    try { sessionStorage.removeItem(k); } catch (e) {}
  },
};

const currency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'MXN';
const money = (cents) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency(),
    minimumFractionDigits: cents % 100 ? 2 : 0,
  }).format(cents / 100);

const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const routes = () => (window.Theme && Theme.routes) || {};
const cartUrl = () => routes().cart_url || root() + 'cart';

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function getCart() {
  const r = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return r.json();
}

/** Ejecuta un cambio en el carrito y avisa al tema para que redibuje el drawer. */
async function cartMutate(source, { action, lines, url, body }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...body, sections: sectionIds().join(','), sections_url: window.location.pathname }),
    }).then((r) => r.json());
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: res.sections,
        didError: Boolean(res.status),
        source: 'vl-cart',
      },
    });
    return res;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

const addItems = (source, items) =>
  cartMutate(source, {
    action: 'add',
    lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
    url: routes().cart_add_url || root() + 'cart/add',
    body: { items },
  });

const removeLine = (source, key) =>
  cartMutate(source, {
    action: 'remove',
    lines: [{ id: key, quantity: 0 }],
    url: routes().cart_change_url || root() + 'cart/change',
    body: { id: key, quantity: 0 },
  });

/* ---------- Temporizador de reserva ---------- */
class VlCartTimer extends HTMLElement {
  connectedCallback() {
    const minutes = Number(this.dataset.minutes) || 10;
    let end = Number(store.get(KEY_END));
    if (!end || end < Date.now() - 30 * 60 * 1000) {
      end = Date.now() + minutes * 60 * 1000;
      store.set(KEY_END, String(end));
    }
    this.end = end;
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() {
    clearInterval(this.interval);
  }
  tick() {
    const left = Math.max(0, Math.round((this.end - Date.now()) / 1000));
    const live = this.querySelector('.vl-timer__live');
    const done = this.querySelector('.vl-timer__done');
    const time = this.querySelector('[data-time]');
    if (left <= 0) {
      if (live) live.hidden = true;
      if (done) done.hidden = false;
      clearInterval(this.interval);
      return;
    }
    if (time) {
      const m = String(Math.floor(left / 60)).padStart(2, '0');
      const s = String(left % 60).padStart(2, '0');
      time.textContent = `${m}:${s}`;
    }
  }
}

/* ---------- Envío prioritario + seguro ---------- */
class VlCartProtection extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.vl-switch');
    this.onClick = () => this.toggle();
    this.button?.addEventListener('click', this.onClick);
    queueMicrotask(() => this.sync());
  }
  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }
  get inCart() {
    return this.dataset.inCart === 'true';
  }
  get unlocked() {
    return this.dataset.unlocked === 'true';
  }
  async run(fn) {
    if (this.busy) return;
    this.busy = true;
    this.classList.add('is-busy');
    try {
      await fn();
    } catch (e) {
      console.warn('[vl-cart] protección', e);
    } finally {
      this.busy = false;
      this.classList.remove('is-busy');
    }
  }
  toggle() {
    if (this.inCart) {
      store.set(KEY_DECLINED, '1');
      store.del(KEY_AUTO);
      this.run(() => removeLine(this, this.dataset.lineKey));
    } else {
      store.del(KEY_DECLINED);
      this.run(() => addItems(this, [{ id: Number(this.dataset.variantId), quantity: 1 }]));
    }
  }
  /** Se ejecuta en cada redibujado del carrito. */
  sync() {
    const eligible = Number(this.dataset.eligible) || 0;
    // Carrito sin productos reales: quitar la protección.
    if (this.inCart && eligible === 0) {
      store.del(KEY_AUTO);
      return this.run(() => removeLine(this, this.dataset.lineKey));
    }
    // Llegó al mínimo: se añade gratis (salvo que la clienta la haya quitado).
    const lastTry = Number(store.get('vl-prot-try')) || 0;
    if (this.unlocked && !this.inCart && !store.get(KEY_DECLINED) && Date.now() - lastTry > 15000) {
      store.set('vl-prot-try', String(Date.now()));
      store.set(KEY_AUTO, '1');
      return this.run(() => addItems(this, [{ id: Number(this.dataset.variantId), quantity: 1 }]));
    }
    // Bajó del mínimo y la habíamos añadido automáticamente: se retira para no cobrarla sin pedirla.
    if (!this.unlocked && this.inCart && store.get(KEY_AUTO)) {
      store.del(KEY_AUTO);
      return this.run(() => removeLine(this, this.dataset.lineKey));
    }
  }
  static get observedAttributes() {
    return ['data-in-cart', 'data-unlocked', 'data-eligible'];
  }
  attributeChangedCallback() {
    if (this.isConnected && this.button) {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.sync(), 50);
    }
  }
}

/* ---------- Upsell "También te puede gustar" ---------- */
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const sizedImage = (src, w = 240) => {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + w;
};

class VlCartUpsell extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.onCartUpdate = (event) => {
      if (event?.promise) event.promise.then(() => this.load()).catch(() => {});
      else this.load();
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.track?.addEventListener('click', (e) => this.onAdd(e));
    this.load();
  }
  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }
  async load() {
    try {
      const cart = await getCart();
      const items = cart.items.filter((i) => i.handle !== PROT_HANDLE);
      if (!items.length) {
        this.hidden = true;
        return;
      }
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const productId = items[0].product_id;
      const signature = productId + ':' + [...inCart].join(',');
      if (signature === this.signature) return;
      this.signature = signature;

      const url = `${root()}recommendations/products.json?product_id=${productId}&limit=10&intent=related`;
      const data = await fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const products = (data.products || [])
        .filter((p) => p.available && !inCart.has(p.id) && p.handle !== PROT_HANDLE)
        .filter((p) => !(p.tags || []).includes('no-upsell'))
        .slice(0, 4);

      if (!products.length) {
        this.hidden = true;
        return;
      }
      this.track.innerHTML = products.map((p) => this.card(p)).join('');
      this.hidden = false;
    } catch (e) {
      console.warn('[vl-cart] upsell', e);
    }
  }
  card(p) {
    const variants = (p.variants || []).filter((v) => v.available);
    const first = variants[0] || p.variants[0];
    const compare = first.compare_at_price && first.compare_at_price > first.price ? first.compare_at_price : null;
    const select =
      variants.length > 1
        ? `<select class="vl-up__select" aria-label="Elige opción">${variants
            .map((v) => `<option value="${v.id}">${esc(v.title)}</option>`)
            .join('')}</select>`
        : `<input type="hidden" value="${first.id}">`;
    return `
      <div class="vl-up">
        <a href="${esc(p.url)}"><img class="vl-up__img" src="${esc(sizedImage(p.featured_image))}" alt="${esc(p.title)}" loading="lazy" width="84" height="112"></a>
        <div class="vl-up__body">
          <a class="vl-up__title" href="${esc(p.url)}">${esc(p.title)}</a>
          <span class="vl-up__price">${compare ? `<s>${money(compare)}</s>` : ''}${money(first.price)}</span>
          ${select}
          <button type="button" class="vl-up__add" data-add>AÑADIR +</button>
        </div>
      </div>`;
  }
  async onAdd(event) {
    const btn = event.target.closest('[data-add]');
    if (!btn || btn.disabled) return;
    const card = btn.closest('.vl-up');
    const field = card.querySelector('select, input[type="hidden"]');
    const id = Number(field?.value);
    if (!id) return;
    btn.disabled = true;
    btn.textContent = 'AÑADIENDO…';
    try {
      await addItems(this, [{ id, quantity: 1 }]);
    } catch (e) {
      console.warn('[vl-cart] añadir upsell', e);
      btn.disabled = false;
      btn.textContent = 'AÑADIR +';
    }
  }
}

if (!customElements.get('vl-cart-timer')) customElements.define('vl-cart-timer', VlCartTimer);
if (!customElements.get('vl-cart-protection')) customElements.define('vl-cart-protection', VlCartProtection);
if (!customElements.get('vl-cart-upsell')) customElements.define('vl-cart-upsell', VlCartUpsell);
